import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { OneTimePasswordResponse } from '@grc/shared';
import { ErrorLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { t } from '../../i18n';
import { createMember } from './admin-api';
import { checkValues, MemberFields, type MemberValues } from './MemberFields';
import { OneTimePassword } from './OneTimePassword';

/**
 * Adding a member (D5, D23): their display name, username and role. The server makes their
 * one-time password, shown here once; they log in with it and choose their own (D20).
 */
export function NewMember() {
  const navigate = useNavigate();
  const [values, setValues] = useState<MemberValues>({
    displayName: '',
    username: '',
    role: 'member',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [created, setCreated] = useState<OneTimePasswordResponse>();
  const displayNameRef = useRef<HTMLInputElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);

  const toList = () => navigate('/admin/members');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const checked = checkValues(values);
    if (checked.kind === 'empty') {
      (checked.field === 'displayName' ? displayNameRef : usernameRef).current?.focus();
      return;
    }
    if (checked.kind === 'rule') {
      setError(t(checked.error));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const { displayName, username } = checked;
      setCreated(await createMember({ displayName, username, role: values.role }));
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  if (created) {
    return (
      <section className="screen screen-narrow">
        <OneTimePassword
          title="oneTime.addedTitle"
          member={created.member}
          password={created.password}
          onDone={toList}
        />
      </section>
    );
  }

  return (
    <section className="screen screen-narrow">
      <h1>{t('newMember.title')}</h1>
      <p className="muted">{t('newMember.intro')}</p>
      <form className="form" onSubmit={onSubmit} noValidate>
        <MemberFields
          values={values}
          onChange={setValues}
          withRole
          displayNameRef={displayNameRef}
          usernameRef={usernameRef}
        />
        <ErrorLine text={error} />
        <button type="submit" className="button" disabled={busy}>
          {busy ? t('newMember.submitting') : t('newMember.submit')}
        </button>
      </form>
      {/* Not while adding: the one-time password would leave with the screen. */}
      <button type="button" className="button button-quiet" onClick={toList} disabled={busy}>
        {t('app.cancel')}
      </button>
    </section>
  );
}
